import * as Sentry from '@sentry/browser';
import React from 'react';
import PropTypes from 'prop-types';
import {
  ChatWidget,
  AVAILABILITY_STATUS,
  expandCollapseManager,
  AppMode,
} from '@wix/chat-web';
import serverApi from '../../../services/serverApi';
import wix from '../../../services/wix';
import firebaseChatService from '../../../services/firebaseChatService';
import typingService from '../../../services/typingService';
import { EDITOR_VIEW_MODE } from '../../../constants/editor-view-mode';
import * as biLogger from '../../../services/biLogger';
import { mediaManager } from '@wix/inbox-common';
import withMobile from '../../../hoc/mobile';
import { withExperiments } from '@wix/wix-experiments-react';
import { CTA_PRODUCT } from '../../../constants/call-to-action';
import { editorSettingsAvatar, theme } from '../../../reducers';
import { connect } from 'react-redux';
import _get from 'lodash/get';
import {
  goOffline,
  collapseWidget,
  expandWidget,
  firstExpand,
  updateHasMessages,
} from '../../../actions';
import { initApp } from '../../../actions/serverApi';
import { chatSdk, Message } from '@wix/chat-sdk';
import { LazyChatRoomScreen } from './LazyChatRoomScreen';
import { loadingScheduler } from '../../../services/loading-scheduler';

class BusinessOnlyWidget extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      instanceId: wix.getInstanceId(),
      shouldShowChat: this.shouldShowChat(props.visibilitySettings),
      alreadySentAwayMessage: false,
      alreadySentLcf: false,
    };
  }

  shouldShowChat = ({ publicVisibility, chatOnboarded, editorVisibility }) =>
    this.isInSiteView() ? publicVisibility && chatOnboarded : editorVisibility;

  async componentDidMount() {
    expandCollapseManager.registerOnExpand(this.handleExpandFromUoU);
    expandCollapseManager.registerOnCollapse(this.handleCollapse);

    try {
      this.showOrHideChat();
      await this.props.initApp({});
    } catch (e) {
      Sentry.captureException(e);
    }

    if (this.props.experiments.enabled('specs.chat.LazilyInteractiveWidget')) {
      void loadingScheduler.onInteractive().then(() => {
        if (this.props.hasMessages) {
          this.onMessageReceived();
        }
      });
    }
  }

  handleExpandFromUoU = () => this.handleExpand('uou');

  componentWillUnmount() {
    expandCollapseManager.unregisterOnExpand(this.handleExpandfromUoU);
    expandCollapseManager.unregisterOnCollapse(this.handleCollapse);
    this.props.firebaseGoOffline();
  }

  showOrHideChat() {
    this.state.shouldShowChat ? this.triggerCollapse() : this.hide();
  }

  isInSiteView = () => this.props.viewMode === EDITOR_VIEW_MODE.SITE;

  isMobile = () => this.props.deviceType === 'mobile';

  handleExpand = async direction => {
    biLogger.expandChatEvent(direction);
    await this.props.expandWidget();

    this.onPostExpand();
  };

  onPostExpand() {
    this.props.firstExpand();
    this.handleSendAwayMessage();
  }

  handleSendAwayMessage() {
    if (
      !this.state.alreadySentAwayMessage &&
      this.props.availabilityStatus === AVAILABILITY_STATUS.AWAY &&
      !this.props.hasMessages &&
      this.props.isContact
    ) {
      const {
        editorSettingsTexts: { offlineChatMessage },
      } = this.props;

      const messageDto = Message.fromText({
        text: offlineChatMessage,
        chatroomId: this.props.businessChatRoomId,
        userId: 'business',
      });

      chatSdk.addMessage(messageDto);
    }

    this.setState({ alreadySentAwayMessage: true });
  }

  handleCollapse = () => !this.isMobile() && this.props.collapseWidget();

  triggerCollapse = () => expandCollapseManager.collapse();

  hide() {
    wix.resizeToHidden();
  }

  handleOnClick = () => {
    if (this.isInSiteView()) {
      firebaseChatService.goOnlineIfOffline();
    }
  };

  onMessageReceived = () => {
    this.props.updateHasMessages(true);

    biLogger.messageReceivedEvent(this.props.shardId);
    this.setState({ shouldShowChat: true });

    if (this.isMobile()) {
      this.props.onMessageReceived();
    } else {
      !this.props.widgetExpanded && this.handleExpand('user');
    }
  };

  onSendMessage = () => biLogger.sendMessageEvent();
  onAttachmentClick = () => biLogger.attachmentClickEvent();
  onCallToActionClick = url => {
    biLogger.callToActionClickEvent(url);
    this.openProductPage(url);
  };

  openProductPage = url => {
    this.isMobile() && this.triggerCollapse();
    const productRegex = /product-page\/([^?/\s]+)/gm;
    const productMatch = productRegex.exec(url);
    const fallbackBehavior = () => window.open(url);
    if (productMatch) {
      const productName = productMatch[1];
      wix.navigateToSection(
        {
          appDefinitionId: CTA_PRODUCT.APP_DEFINITION_ID,
          sectionId: CTA_PRODUCT.SECTION_ID,
        },
        productName,
        fallbackBehavior,
      );
    } else {
      fallbackBehavior();
    }
  };

  onChatDisplayed = () => {
    if (this.state.shouldShowChat) {
      const asRounded = _get(
        this.props.editorSettingsLayout,
        'collapsedAsRounded',
        false,
      );
      const layoutName = asRounded
        ? 'collapsedAsRounded'
        : 'selectedRadiusOption';

      biLogger.chatDisplayedEvent(this.props.deviceType, layoutName);
    }
  };

  handleUploadFile = async file => {
    const { token, url, folderId } = await serverApi.getUploadToken();
    const isNewGen = this.isNewGenMediaEnabled();
    const effectiveFolderId = isNewGen ? folderId : undefined;
    return mediaManager.uploadFileToMedia({
      file,
      token,
      uploadUrlApi: url,
      folderId: effectiveFolderId,
    });
  };

  isNewGenMediaEnabled = () => {
    return (
      this.props.experiments.enabled('specs.media.MediaManager3') &&
      this.props.experiments.enabled('specs.crm.WixChatUsingMM3Spec')
    );
  };

  eventHooks = {
    onSendMessage: () => this.onSendMessage(),
    onMessageReceived: () => this.onMessageReceived(),
    onAttachmentClick: () => this.onAttachmentClick(),
    onChatDisplayed: () => this.onChatDisplayed(),
    onTyping: () => typingService.notifyTyping(),
    onCallToActionClick: url => this.onCallToActionClick(url),
  };

  render() {
    const { shouldShowChat, instanceId } = this.state;
    const {
      themeData,
      locale,
      visibilitySettings,
      isChatSdkInit,
      businessChatRoomId,
      availabilityStatus,
      isBranded,
      widgetExpanded,
    } = this.props;
    const {
      editorSettingsTexts: {
        onlineTitle,
        onlineMessage,
        offlineTitle,
        offlineMessage,
      },
    } = this.props;
    const avatar = this.props.editorSettingsAvatar;
    const dummyMode = this.isInSiteView() && !visibilitySettings.chatOnboarded;
    const isMobile = this.isMobile();
    const isAvailable = availabilityStatus !== AVAILABILITY_STATUS.AWAY;

    const shouldRender =
      isChatSdkInit ||
      this.props.experiments.enabled('specs.chat.LazilyLoadFirebase');

    return shouldRender ? (
      <div onClick={this.handleOnClick}>
        <ChatWidget
          isExpanded={isMobile || widgetExpanded}
          theme={themeData}
          onlineTitle={isAvailable ? onlineTitle : offlineTitle}
          onlineMessage={isAvailable ? onlineMessage : offlineMessage}
          avatar={avatar}
          eventHooks={this.eventHooks}
          available={shouldShowChat}
          isMobile={isMobile}
          locale={locale}
          availabilityStatus={availabilityStatus || undefined}
        >
          <LazyChatRoomScreen
            chatroomId={businessChatRoomId}
            dummyMode={dummyMode}
            onUploadFile={this.handleUploadFile}
            instanceId={instanceId}
            banner={isBranded ? true : undefined}
            unfoldWhenEmpty={isAvailable}
            appMode={AppMode.Widget}
            locale={locale}
            theme={themeData}
          />
        </ChatWidget>
      </div>
    ) : null;
  }
}

BusinessOnlyWidget.propTypes = {
  visibilitySettings: PropTypes.object.isRequired,
  themeData: PropTypes.object.isRequired,
  viewMode: PropTypes.oneOf([EDITOR_VIEW_MODE.SITE, EDITOR_VIEW_MODE.PREVIEW]),
  locale: PropTypes.string,
  experiments: PropTypes.object,
  isMobileOpen: PropTypes.bool,
  onMessageReceived: PropTypes.func,
  editorSettingsLayout: PropTypes.any,
  editorSettingsTexts: PropTypes.any,
  editorSettingsAvatar: PropTypes.any,
  firebaseGoOffline: PropTypes.func,
  initApp: PropTypes.func,
  shardId: PropTypes.string,
  businessChatRoomId: PropTypes.string,
  isChatSdkInit: PropTypes.bool,
  deviceType: PropTypes.string,
  availabilityStatus: PropTypes.oneOf([
    AVAILABILITY_STATUS.AVAILABLE,
    AVAILABILITY_STATUS.AWAY,
  ]),
  isBranded: PropTypes.bool,
  widgetExpanded: PropTypes.bool,
  expandWidget: PropTypes.func,
  collapseWidget: PropTypes.func,
  hasMessages: PropTypes.bool,
  updateHasMessages: PropTypes.func,
};

function mapStateToProps(state) {
  return {
    themeData: theme(state),
    editorSettingsAvatar: editorSettingsAvatar(state),
    editorSettingsTexts: state.editorSettingsTexts,
    editorSettingsLayout: state.editorSettingsLayout,
    visibilitySettings: state.visibilitySettings,
    viewMode: state.viewMode,
    locale: state.locale,
    shardId: state.shardId,
    businessChatRoomId: state.businessChatRoomId,
    isChatSdkInit: state.isChatSdkInit,
    deviceType: state.deviceType,
    availabilityStatus: state.availabilityStatus,
    isBranded: state.isBranded,
    widgetExpanded: state.widgetExpanded,
    isContact: state.isLcfMandatory.isContact,
    hasMessages: state.hasMessages,
  };
}

const mapDispatchToProps = dispatch => ({
  firebaseGoOffline: () => dispatch(goOffline()),
  initApp: data => dispatch(initApp(data)),
  expandWidget: () => dispatch(expandWidget()),
  collapseWidget: () => dispatch(collapseWidget()),
  firstExpand: () => dispatch(firstExpand()),
  updateHasMessages: hasMessages => dispatch(updateHasMessages(hasMessages)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withExperiments(withMobile(BusinessOnlyWidget)));
